import { FC, useState, useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close'

import {
  ModalOverlay,
  ModalContainer,
  ModalContent,
  ModalTitleRow,
  ModalTitleWrapper,
  ModalTitle,
  CloseButton,
  InputWrapper,
  InputLabel,
  InputLabelText,
  TextInput,
  ModalActions,
  CancelButton,
  CancelButtonText,
  SaveButton,
  SaveButtonText
} from './renameChatModal.styles'

interface RenameChatModalProps {
  isOpen: boolean
  currentName: string
  onClose: () => void
  onSave: (newName: string) => Promise<void> | void
}

const RenameChatModal: FC<RenameChatModalProps> = ({ isOpen, currentName, onClose, onSave }) => {
  const [name, setName] = useState(currentName)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setName(currentName)
      setIsSaving(false)
    }
  }, [isOpen, currentName])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose()
    }
  }

  const handleSave = async () => {
    if (name.trim() && !isSaving) {
      setIsSaving(true)
      await onSave(name.trim())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSave()
    } else if (e.key === 'Escape' && !isSaving) {
      onClose()
    }
  }

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalContent>
          <ModalTitleRow>
            <ModalTitleWrapper>
              <ModalTitle>Rename Chat</ModalTitle>
            </ModalTitleWrapper>
            <CloseButton onClick={onClose} disabled={isSaving}>
              <CloseIcon sx={{ fontSize: 24 }} />
            </CloseButton>
          </ModalTitleRow>

          <InputWrapper>
            <InputLabel>
              <InputLabelText>Chat name</InputLabelText>
            </InputLabel>
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter chat name"
              disabled={isSaving}
            />
          </InputWrapper>
        </ModalContent>

        <ModalActions>
          <CancelButton
            onClick={onClose}
            style={{ opacity: isSaving ? 0.5 : 1, pointerEvents: isSaving ? 'none' : 'auto' }}
          >
            <CancelButtonText>Cancel</CancelButtonText>
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            style={{ opacity: isSaving ? 0.5 : 1, pointerEvents: isSaving ? 'none' : 'auto' }}
          >
            <SaveButtonText>{isSaving ? 'Saving...' : 'Save'}</SaveButtonText>
          </SaveButton>
        </ModalActions>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default RenameChatModal
